
import React from "react";
import { Subscription } from "@/types/subscription";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2 } from "lucide-react";

interface SubscriptionCardProps {
  subscription: Subscription;
}

export default function SubscriptionCard({ subscription }: SubscriptionCardProps) {
  // Helper to format the bin day with proper capitalization
  const formatBinDay = (day: string) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  // Determine rates based on business vs residential
  const weeklyRate = subscription.isBusiness ? 20 : 5;
  const annualPrice = subscription.isBusiness ? 840 : 210;
  const isAnnual = subscription.isAnnual;
  
  // Savings calculation
  const savingsPercentage = 20.75;

  return (
    <Card className="mb-4 hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="mb-2 md:mb-0">
            <div className="space-y-1">
              <h3 className="font-semibold text-lg">{subscription.customerName}</h3>
              {subscription.isBusiness && subscription.businessName && (
                <div className="flex items-center gap-1 text-blue-700 font-medium">
                  <Building2 className="h-4 w-4" />
                  <span>{subscription.businessName}</span>
                </div>
              )}
              <p className="text-gray-600 text-sm">{subscription.email}</p>
              <p className="text-gray-600 text-sm">{subscription.address}</p>
              <p className="text-gray-600 text-sm">{subscription.postcode}</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="bg-gray-100 px-3 py-1 rounded-full text-sm">
              {subscription.multipleDays ? "Multiple Collection Days" : `${formatBinDay(subscription.binDay)} Collection`}
            </div>
            <div className="text-right">
              <p className="font-medium">£{subscription.totalPaid.toFixed(2)} paid</p>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">
                  {isAnnual ? (
                    <span>£{annualPrice}/year <span className="text-green-600">({savingsPercentage}% off)</span></span>
                  ) : (
                    `£${weeklyRate}/week`
                  )}
                  {subscription.isBusiness && " per bin"}
                </p>
                <div className="flex gap-1">
                  {isAnnual && (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      Annual
                    </Badge>
                  )}
                  {subscription.isBusiness && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      <Building2 className="h-3 w-3 mr-1" />
                      Business
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
